import * as React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
const HousingPressureIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M3 21h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M5 21V10l7-7 7 7v11" fill="var(--accent)" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M9 21v-4a3 3 0 0 1 6 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="18" cy="7" r="3" fill="var(--accent)" />
    <path d="M16 9l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
const CostOfLivingIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M3 10h18l-2 11H5L3 10z" fill="var(--accent)" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M6 10V5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="14" r="3" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1" />
    <path d="M12 12v4M10 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
const ValueForMoneyIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="9" fill="var(--accent)" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 7v10M9 10l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 17h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
const RentalExposureIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="4" y="4" width="16" height="16" rx="2" fill="var(--accent)" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 16h16" stroke="currentColor" strokeWidth="1.5" />
  </svg>;
const solutions = [{
  title: "Housing Pressure Index",
  description: "Real-time monitoring of mortgage stress and rental affordability across Australian capital cities.",
  icon: HousingPressureIcon,
  link: "/HousingPressureIndexPage"
}, {
  title: "Cost of Living Exposure Index",
  description: "A comprehensive basket analysis of essential household goods and services price movements.",
  icon: CostOfLivingIcon,
  link: "/CostOfLivingIndexPage"
}, {
  title: "Value for Money Index",
  description: "Quantifying the purchasing power of the Australian dollar against historical service quality benchmarks.",
  icon: ValueForMoneyIcon,
  link: "/ValueForMoneyIndexPage"
}, {
  title: "Rental Exposure Index",
  description: "Tracking the percentage of household income dedicated to securing primary residential leases.",
  icon: RentalExposureIcon,
  link: "/RentalExposureIndexPage"
}];
export default function OurSignatureSolutions() {
  return <section data-section-id="223689" id="the-index-suite" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24">
      <div className="mx-auto max-w-7xl">
        <div className="mb-16 md:mb-24">
          <h2 className="font-default text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl text-primary">
            The Index Suite
          </h2>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            See the ratings scale below to understand how index scores are interpreted across severity levels.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {solutions.map((solution, i) => <Card key={i} data-index={i} className="group relative flex min-h-[320px] flex-col border-0 border-t border-border bg-background p-0 pt-8 transition-all duration-300 hover:bg-primary hover:border-primary">
              <div className="px-6 pb-6 md:px-8 md:pb-8 flex flex-col h-full">
                <solution.icon className="size-12 text-muted-foreground transition-all duration-300 group-hover:scale-110 group-hover:text-primary-foreground" />
                
                <h3 className="font-default mt-12 mb-4 text-[32px] font-semibold leading-tight tracking-tight text-primary transition-colors duration-300 group-hover:text-primary-foreground">
                  {solution.title}
                </h3>
                
                <p className="font-default text-muted-foreground text-base leading-relaxed transition-colors duration-300 group-hover:text-primary-foreground/80">
                  {solution.description}
                </p>

                <div className="mt-auto pt-8 overflow-hidden">
                  <Link to={solution.link} className="inline-flex items-center gap-2 text-sm font-medium text-primary opacity-0 -translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-primary-foreground">
                    View Data <ArrowRight className="size-4" />
                  </Link>
                </div>
              </div>
            </Card>)}
        </div>
      </div>
    </section>;
}